import { DefaultHeaderCell } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/DefaultHeaderCell';
import { NewDefaultCell } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/NewDefaultCell';
import { TableColumnBuilder } from '@dealstack/frontend/app/pages/Valuations/components/table/columnBuilder/columnBuilder';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { CurrencyType } from '@dealstack/shared/lib/generated';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { SecurityComponentInfo } from '@dealstack/frontend/app/pages/Valuations/shared/securityComponentInfo.types';
import { BreakpointCellWrapper } from './BreakpointCellWrapper';
import {
	BreakpointSummaryColumnKey,
	BreakpointSummaryRow,
	BreakpointSummaryRowType,
} from './BreakpointSummaryTransformer';

const DefaultHeaderCellWrapper = (props: {
	value: string;
	isLastColumn?: boolean;
	isSubColumn?: boolean;
	isLastSubColumn?: boolean;
	alignment?: 'start' | 'end';
	enableFilter?: boolean;
	enableExpanding?: boolean;
	filterValue?: string;
	onFilterChange?: (value: string) => void;
	filterPlaceholder?: string;
	filterComponent?: React.ReactElement;
	isExpanded?: boolean;
	onToggleClick?: () => void;
}) => {
	return <DefaultHeaderCell {...props} disableBorders={true} />;
};

const DefaultCellWrapper = (props: {
	value: unknown;
	formatter?: (value: unknown) => string;
	alignEnd?: boolean;
	isLastRow?: boolean;
	isLastColumn?: boolean;
	isSubColumn?: boolean;
	isLastSubColumn?: boolean;
	paddingLeft?: string;
	indicator?: boolean;
	columnIndex?: number;
}) => {
	const {
		value,
		alignEnd,
		isLastRow,
		isLastColumn,
		isSubColumn,
		isLastSubColumn,
		formatter,
		paddingLeft,
		indicator,
		columnIndex,
	} = props;
	return (
		<NewDefaultCell
			value={value as string | number | null}
			alignEnd={alignEnd}
			isLastRow={isLastRow}
			isLastColumn={isLastColumn}
			isSubColumn={isSubColumn}
			isLastSubColumn={isLastSubColumn}
			formatter={formatter}
			disableBorders={true}
			paddingLeft={paddingLeft}
			indicator={indicator}
			columnIndex={columnIndex}
		/>
	);
};

const BreakpointCellWrapperForColumn = (props: Record<string, unknown>) => {
	const { value, tanstackRow, columnIndex } = props as {
		value: string | number | null;
		tanstackRow: unknown;
		columnIndex?: number;
	};
	return (
		<BreakpointCellWrapper value={value} tanstackRow={tanstackRow} columnIndex={columnIndex} />
	);
};

interface CreateBreakpointSummaryTableColumnsParams {
	openSecurityOverrideDrawer: (selectedSecurityComponent: SecurityComponentInfo) => void;
	currency: CurrencyType;
}

function createBreakpointSummaryTableColumns(
	params: CreateBreakpointSummaryTableColumnsParams,
	tValuations: ReturnType<typeof useTranslationValuations>
): ColumnDef<BreakpointSummaryRow>[] {
	const config = {
		showFilterInputs: false,
		columnFilters: [],
		updateColumnFilter: () => {
			// No-op for breakpoint summary table
		},
		toggleSubColumns: () => {
			// No-op for breakpoint summary table
		},
		hasVisibleSubColumns: () => false,
		isLastSubColumn: () => false,
		components: {
			HeaderCell: DefaultHeaderCellWrapper,
			DefaultCell: DefaultCellWrapper,
		},
	};

	const builder = new TableColumnBuilder<BreakpointSummaryRow>(config);

	// Security column with custom cell
	builder.addColumnWithCustomCell({
		id: BreakpointSummaryColumnKey.SECURITY,
		header: tValuations('breakpoint_summary.breakdown_column_header'),
		accessor: (row) => row.values[BreakpointSummaryColumnKey.SECURITY],
		alignment: 'start',
		cellComponent: DefaultCellWrapper,
		cellPropsMapper: ({ value, tanstackRow, columnIndex }) => {
			const isBreakpointSecurityColumn =
				tanstackRow.original.metadata?.type ===
				BreakpointSummaryRowType.BREAKPOINT_SECURITY;

			return {
				value: value as string | null,
				alignEnd: false,
				indicator: isBreakpointSecurityColumn,
				paddingLeft: isBreakpointSecurityColumn
					? `${(tanstackRow.original.metadata?.depth ?? 0) * 2}rem`
					: undefined,
				columnIndex,
			};
		},
	});

	// From column
	builder.addColumnWithCustomCell({
		id: BreakpointSummaryColumnKey.FROM,
		header: tValuations('breakpoint_summary.from_column_header', {
			currency: params.currency,
		}),
		accessor: (row) => row.values[BreakpointSummaryColumnKey.FROM],
		alignment: 'end',
		cellComponent: DefaultCellWrapper,
		cellPropsMapper: ({ value, columnIndex }) => ({
			value: value as string | null,
			alignEnd: true,
			columnIndex,
		}),
	});

	// To column
	builder.addColumnWithCustomCell({
		id: BreakpointSummaryColumnKey.TO,
		header: tValuations('breakpoint_summary.to_column_header', {
			currency: params.currency,
		}),
		accessor: (row) => row.values[BreakpointSummaryColumnKey.TO],
		alignment: 'end',
		cellComponent: DefaultCellWrapper,
		cellPropsMapper: ({ value, columnIndex }) => ({
			value: value as string | null,
			alignEnd: true,
			columnIndex,
		}),
	});

	// Proceeds in tier column with custom cell for overrides
	builder.addColumnWithCustomCell({
		id: BreakpointSummaryColumnKey.PROCEEDS_IN_TIER,
		header: tValuations('breakpoint_summary.proceeds_column_header', {
			currency: params.currency,
		}),
		accessor: (row) => row.values[BreakpointSummaryColumnKey.PROCEEDS_IN_TIER],
		alignment: 'end',
		cellComponent: BreakpointCellWrapperForColumn,
		cellPropsMapper: ({
			value,
			tanstackRow,
			column,
			table,
			isLastRow,
			isLastColumn,
			columnIndex,
		}) => ({
			value,
			tanstackRow,
			column,
			table,
			isLastRow,
			isLastColumn,
			columnIndex,
		}),
	});

	// Remaining to distribute column
	builder.addColumnWithCustomCell({
		id: BreakpointSummaryColumnKey.REMAINING_TO_DISTRIBUTE,
		header: tValuations('breakpoint_summary.remaining_to_distribute_column_header', {
			currency: params.currency,
		}),
		accessor: (row) => row.values[BreakpointSummaryColumnKey.REMAINING_TO_DISTRIBUTE],
		alignment: 'end',
		cellComponent: DefaultCellWrapper,
		cellPropsMapper: ({ value, columnIndex }) => ({
			value: value as string | null,
			alignEnd: true,
			columnIndex,
		}),
	});

	// Participating securities column
	builder.addColumnWithCustomCell({
		id: BreakpointSummaryColumnKey.PARTICIPATING_SECURITIES,
		header: tValuations('breakpoint_summary.participating_securities_column_header'),
		accessor: (row) => row.values[BreakpointSummaryColumnKey.PARTICIPATING_SECURITIES],
		alignment: 'end',
		cellComponent: DefaultCellWrapper,
		cellPropsMapper: ({ value, columnIndex }) => ({
			value: value as string | null,
			alignEnd: true,
			columnIndex,
		}),
	});

	// Proceeds per security column with custom cell for overrides
	builder.addColumnWithCustomCell({
		id: BreakpointSummaryColumnKey.PROCEEDS_PER_SECURITY,
		header: tValuations('breakpoint_summary.proceeds_per_security_column_header', {
			currency: params.currency,
		}),
		accessor: (row) => row.values[BreakpointSummaryColumnKey.PROCEEDS_PER_SECURITY],
		alignment: 'end',
		cellComponent: BreakpointCellWrapperForColumn,
		cellPropsMapper: ({
			value,
			tanstackRow,
			column,
			table,
			isLastRow,
			isLastColumn,
			columnIndex,
		}) => ({
			value,
			tanstackRow,
			column,
			table,
			isLastRow,
			isLastColumn,
			columnIndex,
		}),
	});

	return builder.build();
}

export function useBreakpointSummaryTableColumns({
	openSecurityOverrideDrawer,
	currency,
}: CreateBreakpointSummaryTableColumnsParams): ColumnDef<BreakpointSummaryRow>[] {
	const tValuations = useTranslationValuations();

	return useMemo(
		() =>
			createBreakpointSummaryTableColumns(
				{ openSecurityOverrideDrawer, currency },
				tValuations
			),
		[openSecurityOverrideDrawer, currency, tValuations]
	);
}
